/* Vector Space — calendar (kwvector.space #calendar).
 *
 * The page is server-rendered; this module renders the one genuinely
 * interactive piece — the dark month grid with per-day morning/afternoon/
 * evening availability and a click-for-detail day popover — into #vs-cal-root
 * from the JSON the view embeds in #vs-calendar-data. It's a vanilla-JS port
 * of design/vector-space-website-v2/site/Calendar.jsx. The "upcoming public
 * events" rail underneath is server-rendered in the template, so it survives
 * with JS disabled; only the grid needs this script.
 *
 * Data shape (vectorspace.views._booking_calendar):
 *   { today: 'YYYY-MM-DD', months: ['YYYY-MM', …],
 *     events: [{ date, access: 'public'|'private', booked: bool,
 *                slots: ['morning', …], title, type, time, badge, link }] }
 * `booked` true → render a bare "Booked" (title is "Private booking"); else the
 * title shows. `badge` (Event.calendar_badge) swaps the title for a brand mark;
 * `link` makes the title an external anchor. Multiple entries may share a date;
 * a full-day booking carries all 3 slots.
 */

import { createIcons, ChevronLeft, ChevronRight, X, ArrowRight, ExternalLink } from 'lucide';

// Icons used inside the grid card (#vs-cal-root). The rail's clock lives
// outside that root and is rendered by the main vectorspace.js bundle.
const CAL_ICONS = { ChevronLeft, ChevronRight, X, ArrowRight, ExternalLink };

const SLOTS = [
    { key: 'morning', label: 'Morning' },
    { key: 'afternoon', label: 'Afternoon' },
    { key: 'evening', label: 'Evening' },
];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
// Workshop pops in nano; every other event type uses the calmer green tint.
const TYPE_TINT = { Workshop: 'var(--nano-500)' };
const POP_W = 300;

const pad = (n) => String(n).padStart(2, '0');
const parseDate = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
const sameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const tintFor = (type) => TYPE_TINT[type] || 'var(--green-300)';

/* Tiny createElement helper. Dynamic strings go through `text` (textContent),
 * never innerHTML, so DB-sourced titles can't inject markup. */
function el(tag, opts = {}, children = []) {
    const node = document.createElement(tag);
    if (opts.class) node.className = opts.class;
    if (opts.text != null) node.textContent = opts.text;
    if (opts.attrs) for (const k in opts.attrs) node.setAttribute(k, opts.attrs[k]);
    if (opts.style) Object.assign(node.style, opts.style);
    if (opts.on) for (const k in opts.on) node.addEventListener(k, opts.on[k]);
    (Array.isArray(children) ? children : [children]).forEach((c) => {
        if (c == null) return;
        node.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
    });
    return node;
}

function icon(name, cls) {
    return el('i', { class: cls, attrs: { 'data-lucide': name } });
}

function initCalendar(root) {
    let data;
    try {
        data = JSON.parse(document.getElementById('vs-calendar-data').textContent);
    } catch (e) {
        return;
    }
    if (!data || !data.months || !data.months.length) return;

    const today = parseDate(data.today);

    // Per-event `badge` (Event.calendar_badge) maps to a brand logo shown in
    // the day cell instead of a long title — keeps recurring programming
    // compact (the full title stays in the popover).
    const BADGE_LOGO = { eastbridge: root.dataset.eaLogo || '', vector: root.dataset.vsLogo || '' };
    const BADGE_ALT = { eastbridge: 'Eastbridge Academy', vector: 'Vector Space' };

    // date string -> [entries]
    const eventsByDate = {};
    data.events.forEach((e) => { (eventsByDate[e.date] = eventsByDate[e.date] || []).push(e); });

    // For a date, which entry (if any) owns each of the three slots.
    const slotMap = (entries) => {
        const m = { morning: null, afternoon: null, evening: null };
        (entries || []).forEach((e) => (e.slots || []).forEach((s) => { if (s in m) m[s] = e; }));
        return m;
    };

    const months = data.months.map((key) => {
        const [y, m] = key.split('-').map(Number);
        return { key, y, m: m - 1, label: new Date(y, m - 1, 1).toLocaleString('en-US', { month: 'long', year: 'numeric' }) };
    });

    let monthIndex = 0;
    let popKey = null; // date string of the open day popover, or null

    const goToApply = () => { document.getElementById('apply')?.scrollIntoView({ behavior: 'smooth' }); };

    function slotBar(sm, isPast) {
        const segs = SLOTS.map((s) => {
            const ev = sm[s.key];
            const cls = ev
                ? 'vs-cal-slotbar__seg vs-cal-slotbar__seg--' + ev.access
                : 'vs-cal-slotbar__seg vs-cal-slotbar__seg--open';
            const label = `${s.label} · ${ev ? (ev.booked ? 'Booked' : ev.title) : 'Open'}`;
            return el('span', { class: cls, attrs: { title: label } });
        });
        return el('div', { class: 'vs-cal-slotbar' + (isPast ? ' vs-cal-slotbar--past' : '') }, segs);
    }

    function badgeNode(key, title) {
        const wrap = el('span', { class: 'vs-cal-badge' });
        const src = BADGE_LOGO[key];
        if (src) {
            wrap.appendChild(el('img', { attrs: { src, alt: title || BADGE_ALT[key] || '', title: title || '' } }));
        } else {
            wrap.appendChild(el('span', { class: 'vs-cal-summary vs-cal-summary--mono', text: title || '' }));
        }
        return wrap;
    }

    function summaryNode(entries) {
        if (!entries || !entries.length) return null;
        // If every booking on the day is the same badged event (e.g. a class,
        // or chess club across morning+afternoon), show that one brand mark.
        const badges = entries.map((e) => e.badge).filter(Boolean);
        if (badges.length === entries.length && new Set(badges).size === 1) {
            return badgeNode(badges[0], entries[0].title);
        }
        if (entries.length === 1) {
            const e = entries[0];
            const full = (e.slots || []).length >= 3;
            if (full) return el('span', { class: 'vs-cal-summary vs-cal-summary--mono', text: 'Full day' });
            return el('span', { class: 'vs-cal-summary', text: e.title });
        }
        return el('span', { class: 'vs-cal-summary vs-cal-summary--mono', text: entries.length + ' bookings' });
    }

    function buildPopover(key, gridWrap, cell, cellIndex, totalRows) {
        const entries = eventsByDate[key] || [];
        const sm = slotMap(entries);
        const popDate = parseDate(key);

        const head = el('div', { class: 'vs-cal-pop__head' }, [
            el('div', { class: 'vs-cal-pop__title', text: popDate.toLocaleString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }) }),
            el('button', { class: 'vs-cal-pop__close', attrs: { 'aria-label': 'Close' }, on: { click: () => { popKey = null; render(); } } }, [icon('x')]),
        ]);

        const slotRows = SLOTS.map((s) => {
            const ev = sm[s.key];
            const swatch = el('span', { class: 'vs-cal-pop__swatch vs-cal-pop__swatch--' + (ev ? ev.access : 'open') });
            const label = el('span', { class: 'vs-cal-pop__slotlabel', text: s.label });
            const valText = ev ? (ev.booked ? 'Booked' : ev.title) : 'Open';
            const valCls = 'vs-cal-pop__slotval' + (ev ? '' : ' vs-cal-pop__slotval--open');
            const val = (ev && !ev.booked && ev.link)
                ? el('a', { class: valCls + ' vs-cal-pop__link', attrs: { href: ev.link, target: '_blank', rel: 'noopener', title: valText } },
                    [document.createTextNode(valText + ' '), icon('external-link', 'vs-cal-pop__linkicon')])
                : el('span', { class: valCls, text: valText, attrs: { title: valText } });
            return el('div', { class: 'vs-cal-pop__slot' }, [swatch, label, val]);
        });
        const slotsBox = el('div', { class: 'vs-cal-pop__slots' }, slotRows);

        const publicCards = entries.filter((e) => e.access === 'public').map((e) =>
            el('div', { class: 'vs-cal-pop__event' }, [
                el('div', { class: 'vs-cal-pop__event-top' }, [
                    e.link
                        ? el('a', { class: 'vs-cal-pop__event-title vs-cal-pop__link', attrs: { href: e.link, target: '_blank', rel: 'noopener' } },
                            [document.createTextNode(e.title + ' '), icon('external-link', 'vs-cal-pop__linkicon')])
                        : el('div', { class: 'vs-cal-pop__event-title', text: e.title }),
                    el('span', { class: 'vs-cal-pop__type', text: e.type, style: { color: tintFor(e.type) } }),
                ]),
                el('div', { class: 'vs-cal-pop__event-time', text: e.time }),
            ])
        );

        const children = [head, slotsBox, ...publicCards];

        const anyOpen = Object.values(sm).some((v) => !v);
        if (anyOpen && popDate >= today) {
            children.push(el('button', { class: 'vs-cal-pop__cta', on: { click: () => { popKey = null; render(); goToApply(); } } }, [
                document.createTextNode('Book this date '), icon('arrow-right'),
            ]));
        }

        // Flip above for the bottom two rows so the popover stays on-screen.
        const row = Math.floor(cellIndex / 7);
        const above = row >= totalRows - 2;
        const pop = el('div', { class: 'vs-cal-pop vs-cal-pop--' + (above ? 'above' : 'below') }, children);
        gridWrap.appendChild(pop);

        // Position relative to the grid wrapper, clamped horizontally.
        const wrapRect = gridWrap.getBoundingClientRect();
        const cellRect = cell.getBoundingClientRect();
        let left = cellRect.left - wrapRect.left + cellRect.width / 2 - POP_W / 2;
        left = Math.max(0, Math.min(left, wrapRect.width - POP_W));
        pop.style.left = left + 'px';
        pop.style.width = POP_W + 'px';
        if (above) {
            pop.style.bottom = (gridWrap.offsetHeight - (cellRect.top - wrapRect.top) + 8) + 'px';
        } else {
            pop.style.top = (cellRect.bottom - wrapRect.top + 8) + 'px';
        }
    }

    function render() {
        const cur = months[monthIndex];

        // Month switcher
        const navBtn = (name, dir, disabled, label) =>
            el('button', {
                class: 'vs-cal-navbtn' + (disabled ? ' is-disabled' : ''),
                attrs: { 'aria-label': label, ...(disabled ? { disabled: 'disabled' } : {}) },
                on: disabled ? {} : { click: () => { monthIndex += dir; popKey = null; render(); } },
            }, [icon(name)]);

        const switcher = el('div', { class: 'vs-cal-switcher' }, [
            el('div', { class: 'vs-cal-switcher__label', text: cur.label }),
            el('div', { class: 'vs-cal-switcher__nav' }, [
                navBtn('chevron-left', -1, monthIndex === 0, 'Previous month'),
                navBtn('chevron-right', 1, monthIndex === months.length - 1, 'Next month'),
            ]),
        ]);

        const weekdays = el('div', { class: 'vs-cal-weekdays' }, WEEKDAYS.map((w) => el('div', { class: 'vs-cal-weekday', text: w })));

        // Day cells
        const firstWeekday = new Date(cur.y, cur.m, 1).getDay();
        const daysInMonth = new Date(cur.y, cur.m + 1, 0).getDate();
        const totalRows = Math.ceil((firstWeekday + daysInMonth) / 7);
        const grid = el('div', { class: 'vs-cal-grid' });

        let index = 0;
        let selectedCell = null;
        let selectedIndex = -1;
        for (let i = 0; i < firstWeekday; i++) { grid.appendChild(el('div', { class: 'vs-cal-cell--blank' })); index++; }
        for (let d = 1; d <= daysInMonth; d++) {
            const key = `${cur.y}-${pad(cur.m + 1)}-${pad(d)}`;
            const entries = eventsByDate[key];
            const sm = slotMap(entries);
            const dt = new Date(cur.y, cur.m, d);
            const isToday = sameDay(dt, today);
            const isPast = dt < today && !isToday;
            const isSelected = popKey === key;
            const hasEv = !!entries;

            const head = el('div', { class: 'vs-cal-cell__head' }, [
                el('span', { class: 'vs-cal-daynum', text: String(d) }),
                hasEv && !isPast
                    ? el('span', { class: 'vs-cal-dot vs-cal-dot--' + (entries.some((e) => e.access === 'public') ? 'public' : 'private') })
                    : null,
            ]);

            const cellIndex = index;
            const cls = 'vs-cal-cell'
                + (isToday ? ' vs-cal-cell--today' : '')
                + (isPast ? ' vs-cal-cell--past' : '')
                + (isSelected ? ' vs-cal-cell--selected' : '');
            const cell = el('button', {
                class: cls,
                attrs: { type: 'button', 'data-date': key },
                on: { click: () => { popKey = popKey === key ? null : key; render(); } },
            }, [head, summaryNode(entries), slotBar(sm, isPast)]);

            grid.appendChild(cell);
            if (isSelected) { selectedCell = cell; selectedIndex = cellIndex; }
            index++;
        }

        const gridWrap = el('div', { class: 'vs-cal-grid-wrap' }, [grid]);

        root.replaceChildren(switcher, weekdays, gridWrap);
        createIcons({ icons: CAL_ICONS, root });

        // Popover needs the cells laid out, so build it after insertion.
        if (popKey && selectedCell) {
            buildPopover(popKey, gridWrap, selectedCell, selectedIndex, totalRows);
            createIcons({ icons: CAL_ICONS, root });
        }
    }

    // Close the popover on outside click / Esc (cells handle their own toggle).
    document.addEventListener('mousedown', (e) => {
        if (!popKey) return;
        if (e.target.closest('.vs-cal-pop') || e.target.closest('.vs-cal-cell')) return;
        popKey = null;
        render();
    });
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && popKey) { popKey = null; render(); }
    });

    render();
}

const root = document.getElementById('vs-cal-root');
if (root) {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => initCalendar(root));
    } else {
        initCalendar(root);
    }
}
